import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {OverlayPanel} from 'primereact/overlaypanel';
import {ColorPicker} from 'primereact/colorpicker';
import {Button} from 'primereact/button';
import {confirmDialog} from 'primereact/confirmdialog';
import {GradientStop, sampleGradient} from '../../services/chart/Functions';
import {DEFAULT_GRADIENT} from '../../redux/slices/ChartStateSlice';

type Props = {
    gradient: GradientStop[];
    onChange: (next: GradientStop[]) => void;
    maxValue: number;
    label?: string;
    // When true, the gradient bar is hidden in the UI and the SVG chart uses
    // the dominant element's color instead of sampling the gradient.
    colorByElement?: boolean;
    onColorByElementChange?: (value: boolean) => void;
};

// Build a CSS linear-gradient string. We prepend a stop at 0 and append a
// stop at 1 that match the first/last stops respectively, so the region
// between the boundary and an inward-moved edge pin renders as a flat color.
const buildCssGradient = (stops: GradientStop[]): string => {
    if (stops.length === 0) return '#ffffff';
    const sorted = [...stops].sort((a, b) => a.position - b.position);
    const parts: string[] = [];
    parts.push(`${sorted[0].color} 0%`);
    for (const s of sorted) parts.push(`${s.color} ${(s.position * 100).toFixed(3)}%`);
    parts.push(`${sorted[sorted.length - 1].color} 100%`);
    return `linear-gradient(to right, ${parts.join(', ')})`;
};

const formatValue = (v: number): string => {
    if (!isFinite(v)) return '–';
    if (v === 0) return '0';
    const abs = Math.abs(v);
    if (abs >= 100) return v.toFixed(0);
    if (abs >= 10) return v.toFixed(1);
    return v.toFixed(2);
};

const GradientEditor: React.FC<Props> = ({gradient, onChange, maxValue, label, colorByElement, onColorByElementChange}) => {
    const trackRef = useRef<HTMLDivElement | null>(null);
    const colorPickerRef = useRef<OverlayPanel | null>(null);

    // Working copy keyed by stable indexes. We always normalize: first stop is
    // the "min edge" (never deletable) and last is the "max edge" (never
    // deletable); both can be dragged inward.
    const sorted = useMemo(
        () => [...gradient].sort((a, b) => a.position - b.position),
        [gradient]
    );

    const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [tempColor, setTempColor] = useState<string>('');
    const [hover, setHover] = useState<{leftPct: number; value: number} | null>(null);
    const draggingIndexRef = useRef<number | null>(null);
    draggingIndexRef.current = draggingIndex;

    const commit = useCallback((next: GradientStop[]) => {
        const sortedNext = [...next].sort((a, b) => a.position - b.position);
        onChange(sortedNext);
    }, [onChange]);

    const positionFromEvent = useCallback((clientX: number): number => {
        const track = trackRef.current;
        if (!track) return 0;
        const rect = track.getBoundingClientRect();
        const t = (clientX - rect.left) / Math.max(1, rect.width);
        return Math.max(0, Math.min(1, t));
    }, []);

    // Mouse drag of a pin: position is clamped between its neighbors so the
    // ordering (and the identity of the first/last "edge" pins) is preserved.
    useEffect(() => {
        if (draggingIndex == null) return;
        const onMove = (e: MouseEvent) => {
            const idx = draggingIndexRef.current;
            if (idx == null) return;
            const t = positionFromEvent(e.clientX);
            const left = idx === 0 ? 0 : sorted[idx - 1].position + 0.0001;
            const right = idx === sorted.length - 1 ? 1 : sorted[idx + 1].position - 0.0001;
            const clamped = Math.max(left, Math.min(right, t));
            const next = sorted.map((s, i) => i === idx ? {...s, position: clamped} : s);
            commit(next);
        };
        const onUp = () => setDraggingIndex(null);
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, [draggingIndex, sorted, positionFromEvent, commit]);

    const handleTrackMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        // Add a new stop where the user clicked, with the interpolated color
        // at that point. Drag starts immediately on the new pin.
        const raw = positionFromEvent(e.clientX);
        // Keep new stops strictly between the existing edge pins so the
        // edge identity (first/last in sorted order) stays stable.
        const leftEdge = sorted[0].position + 0.0005;
        const rightEdge = sorted[sorted.length - 1].position - 0.0005;
        const t = Math.max(leftEdge, Math.min(rightEdge, raw));
        const color = sampleGradient(sorted, t);
        const next = [...sorted, {position: t, color}].sort((a, b) => a.position - b.position);
        commit(next);
        const newIdx = next.findIndex(s => s.position === t && s.color === color);
        if (newIdx >= 0) setDraggingIndex(newIdx);
    };

    const handlePinMouseDown = (e: React.MouseEvent, idx: number) => {
        e.stopPropagation();
        setDraggingIndex(idx);
    };

    const handleDeleteStop = (idx: number) => {
        // Edge pins (first and last in sorted order) are never deletable.
        if (idx === 0 || idx === sorted.length - 1) return;
        const next = sorted.filter((_, i) => i !== idx);
        commit(next);
    };

    const openColorPicker = (e: React.MouseEvent, idx: number) => {
        e.stopPropagation();
        setEditingIndex(idx);
        setTempColor((sorted[idx].color || '').replace('#', ''));
        colorPickerRef.current?.toggle(e);
    };

    const css = useMemo(() => buildCssGradient(sorted), [sorted]);

    const handleReset = () => {
        confirmDialog({
            message: 'Reset the gradient to its default colors? Your current pins and colors will be lost.',
            header: 'Reset gradient',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Reset',
            rejectLabel: 'Cancel',
            accept: () => commit(DEFAULT_GRADIENT.map(s => ({...s}))),
        });
    };

    return (
        <div style={{userSelect: 'none'}}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 6px'}}>
                {label ? <h3 style={{margin: 0}}>{label}</h3> : <span/>}
                <Button
                    icon="pi pi-refresh"
                    className="p-button-text p-button-sm p-button-secondary"
                    tooltip="Reset gradient to default colors"
                    tooltipOptions={{position: 'top'}}
                    onClick={handleReset}
                    style={{padding: '2px 6px'}}
                    disabled={!!colorByElement}
                />
            </div>
            {onColorByElementChange && (
                <label style={{display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', cursor: 'pointer', marginBottom: '4px'}}>
                    <input
                        type="checkbox"
                        checked={!!colorByElement}
                        onChange={(e) => onColorByElementChange(e.target.checked)}
                    />
                    Use dominant element color
                </label>
            )}
            {colorByElement ? null : (
            <>
            <div style={{position: 'relative', padding: '14px 0 28px'}}>
                <div
                    ref={trackRef}
                    onMouseDown={handleTrackMouseDown}
                    onMouseMove={(e) => {
                        const t = positionFromEvent(e.clientX);
                        setHover({leftPct: t * 100, value: t * maxValue});
                    }}
                    onMouseLeave={() => setHover(null)}
                    style={{
                        height: '18px',
                        borderRadius: '4px',
                        background: css,
                        border: '1px solid #999',
                        cursor: 'copy',
                        position: 'relative',
                        overflow: 'visible',
                    }}
                >
                    {hover && draggingIndex == null && (
                        <div style={{
                            position: 'absolute',
                            left: `${hover.leftPct.toFixed(3)}%`,
                            top: '-26px',
                            transform: 'translateX(-50%)',
                            padding: '2px 6px',
                            fontSize: '10px',
                            background: 'rgba(0,0,0,0.78)',
                            color: '#fff',
                            borderRadius: '3px',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                            zIndex: 3,
                        }}>
                            {formatValue(hover.value)}
                        </div>
                    )}
                    {sorted.map((stop, idx) => {
                        const isEdge = idx === 0 || idx === sorted.length - 1;
                        return (
                            <div key={idx}
                                 onMouseDown={(e) => handlePinMouseDown(e, idx)}
                                 onDoubleClick={() => handleDeleteStop(idx)}
                                 onContextMenu={(e) => { e.preventDefault(); handleDeleteStop(idx); }}
                                 title={isEdge ? 'Edge pin (cannot be deleted)' : 'Double-click to delete'}
                                 style={{
                                     position: 'absolute',
                                     left: `calc(${(stop.position * 100).toFixed(3)}% - 7px)`,
                                     top: '-10px',
                                     width: '14px',
                                     height: '38px',
                                     cursor: 'ew-resize',
                                     display: 'flex',
                                     flexDirection: 'column',
                                     alignItems: 'center',
                                     zIndex: 2,
                                 }}>
                                <div
                                    onClick={(e) => openColorPicker(e, idx)}
                                    style={{
                                        width: '12px',
                                        height: '12px',
                                        background: stop.color,
                                        border: `2px solid ${isEdge ? '#222' : '#fff'}`,
                                        borderRadius: isEdge ? '2px' : '50%',
                                        boxShadow: '0 0 2px rgba(0,0,0,0.6)',
                                        cursor: 'pointer',
                                    }}
                                />
                                <div style={{
                                    width: '2px',
                                    height: '24px',
                                    background: '#444',
                                    marginTop: '-1px',
                                }}/>
                            </div>
                        );
                    })}
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '10px', color: '#666'}}>
                    <span>0</span>
                    <span>{formatValue(maxValue)}</span>
                </div>
            </div>

            <div style={{fontSize: '10px', color: '#666', marginTop: '-4px'}}>
                Click on the bar = add pin · Double-click / right-click = delete pin · Click on the dot = change color
            </div>
            </>
            )}

            <OverlayPanel ref={colorPickerRef} dismissable onHide={() => setEditingIndex(null)}>
                <div className="flex flex-column gap-3 p-1">
                    <ColorPicker
                        value={tempColor}
                        onChange={(e) => { if (e.value) setTempColor(e.value as string); }}
                        inline
                    />
                    <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                        <span style={{fontSize: '12px', color: '#666'}}>#</span>
                        <input
                            type="text"
                            maxLength={6}
                            value={tempColor}
                            onChange={(e) => setTempColor(e.target.value.replace(/[^0-9a-fA-F]/g, ''))}
                            style={{
                                width: '80px',
                                padding: '4px 6px',
                                fontSize: '12px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                fontFamily: 'monospace',
                                outline: 'none',
                                textTransform: 'uppercase',
                            }}
                        />
                        <div style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '3px',
                            background: tempColor.length === 6 ? `#${tempColor}` : 'transparent',
                            border: '1px solid #999',
                            flexShrink: 0,
                        }}/>
                    </div>
                    <div className="flex justify-content-between gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text p-button-sm p-button-secondary"
                            onClick={() => colorPickerRef.current?.hide()}
                        />
                        <Button
                            label="Ok"
                            icon="pi pi-check"
                            className="p-button-sm"
                            onClick={() => {
                                if (editingIndex != null && tempColor.length === 6) {
                                    const next = sorted.map((s, i) => i === editingIndex
                                        ? {...s, color: `#${tempColor}`}
                                        : s);
                                    commit(next);
                                }
                                colorPickerRef.current?.hide();
                            }}
                        />
                    </div>
                </div>
            </OverlayPanel>
        </div>
    );
};

export default GradientEditor;
